import { createConfig, http } from "wagmi";
import { bscTestnet } from "wagmi/chains";
import { injected } from "wagmi/connectors";
import { defineChain } from "viem";

import { runtimeConfig } from "./runtime-config";

/**
 * The wallet stack.
 *
 * Two deliberate narrowings against the claim portal's setup, both because this
 * is staging:
 *
 *  - **Injected connectors only.** No RainbowKit, no WalletConnect. The portal
 *    needs them because real holders arrive with whatever wallet they have; the
 *    hub is driven by a handful of test wallets in a desktop browser. Pulling
 *    in the WalletConnect stack would also drag `@coinbase/cdp-sdk`'s optional
 *    peers, which is the reason the portal carries an `x402-stub.ts` at all.
 *  - **One chain, chosen at runtime.** The portal ships both networks in one
 *    image; the hub cannot run on mainnet, so offering a mainnet chain object
 *    would only make a wrong-network state reachable that has no valid
 *    resolution.
 *
 * Transport is `/api/rpc` on this origin, never a public endpoint directly.
 * That is what gives the browser the server's fallback rotation and keeps
 * `connect-src` on 'self' for the primary path.
 */

/** Anvil. Not in viem's chain list, and the local loop needs it constantly. */
const localhost = defineChain({
  id: 31337,
  name: "Localhost",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: ["http://127.0.0.1:8545"] } },
});

const { chainId } = runtimeConfig();

export const activeChain = chainId === 97 ? bscTestnet : localhost;

export const wagmiConfig = createConfig({
  chains: [activeChain],
  connectors: [injected()],
  /*
    Both ids are keyed even though only one chain is offered.

    `activeChain` is chosen at runtime, so its type is the union of the two and
    wagmi asks for a transport per member. Both point at the same relay — the
    server decides which chain it is actually talking to, and a second entry
    here cannot make a chain reachable that the config did not offer.
  */
  transports: {
    [bscTestnet.id]: http("/api/rpc"),
    [localhost.id]: http("/api/rpc"),
  },
  // The hub is a staging tool people open, test and close. Restoring a
  // connection they did not ask to restore makes "which wallet am I on?"
  // harder to answer, which is the question every staging bug starts with.
  ssr: false,
});

declare module "wagmi" {
  interface Register {
    config: typeof wagmiConfig;
  }
}
